import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { afterNextRender, provideAppInitializer, ApplicationConfig, inject, Injector, importProvidersFrom, isDevMode, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withComponentInputBinding } from '@angular/router';
import { Sym70Preset } from '@/theme/sym70-preset';
import { ThemeRuntimeService } from '@/theme/theme-runtime.service';

import { providePrimeNG } from 'primeng/config';
import { NgxSpinnerModule } from 'ngx-spinner';
import { appRoutes } from './app.routes';
import MSALInstanceFactory from '@/core/msal/instance.factory';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService } from '@azure/msal-angular';
import MSALGuardConfigFactory from '@/core/msal/guard.config';
import MSALInterceptorConfigFactory from '@/core/msal/interceptor.factory.config';
import { AuthErrorInterceptor } from '@/core/interceptors/auth-error.interceptor';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CategoryTypeEffects } from '@/core/store/category-type/category-type.effects';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideStore } from '@ngrx/store';
import { categoryTypeReducer } from '@/core/store/category-type/category-type.reducer';
import { ProjectSubtypeEffects } from '@/core/store/project-subtype/project-subtype.effects';
import { ProjectTypeEffects } from '@/core/store/project-type/project-type.effects';
import { projectTypeReducer } from '@/core/store/project-type/project-type.reducer';
import { projectSubTypeReducer } from '@/core/store/project-subtype/project-subtype.reducer';
import { UnitMeasureEffects } from '@/core/store/unit-measure/unit-measure.effects';
import { unitMeasureReducer } from '@/core/store/unit-measure/unit-measure.reducer';
import { apuStandardReducer } from '@/core/store/apu-standard/apu-standard.reducer';
import { ApuStandardEffects } from '@/core/store/apu-standard/apu-standard.effects';
import { apuStandardCategoryTypeReducer } from '@/core/store/apu-standard-category-type/apu-standard-category-type.reducer';
import { ApuStandardCategoryTypeEffects } from '@/core/store/apu-standard-category-type/apu-standard-category-type.effects';
import { apuStandardCategoryReducer } from '@/core/store/apu-standard-category/apu-standard-category.reducer';
import { ApuStandardCategoryEffects } from '@/core/store/apu-standard-category/apu-standard-category.effects';
import { apuStandardResourceReducer } from '@/core/store/apu-standard-resource/apu-standard-resource.reducer';
import { ApuStandardResourceEffects } from '@/core/store/apu-standard-resource/apu-standard-resource.effects';
import { tenderReducer } from '@/core/store/tender/tender.reducer';
import { TenderEffects } from '@/core/store/tender/tender.effects';
import { apuCategoryReducer } from '@/core/store/apu-category/apu-category.reducer';
import { apuCategoryTypeReducer } from '@/core/store/apu-category-type/apu-category-type.reducer';
import { apuResourceReducer } from '@/core/store/apu-resource/apu-resource.reducer';
import { ApuResourceEffects } from '@/core/store/apu-resource/apu-resource.effects';
import { ApuCategoryEffects } from '@/core/store/apu-category/apu-category.effects';
import { ApuCategoryTypeEffects } from '@/core/store/apu-category-type/apu-category-type.effects';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { productReducer } from '@/core/store/product/product.reducer';
import { ProductEffects } from '@/core/store/product/product.effects';
import { tenderSaleReducer } from '@/core/store/tender-sale/tender-sale.reducer';
import { TenderSaleEffects } from '@/core/store/tender-sale/tender-sale.effects';
import { projectChangeControlReducer } from '@/core/store/project-change-control/project-change-control.reducer';
import { ProjectChangeControlEffects } from '@/core/store/project-change-control/project-change-control.effects';
import { projectReducer } from '@/core/store/project/project.reducer';
import { ProjectEffects } from '@/core/store/project/project.effects';
import { lpsReducer } from '@/core/store/lps/lps.reducer';
import { LpsEffects } from '@/core/store/lps/lps.effects';
import { DialogService } from 'primeng/dynamicdialog';
import { budgetControlItemReducer } from '@/core/store/budget-control-item/budget-control-item.reducer';
import { BudgetControlItemEffects } from '@/core/store/budget-control-item/budget-control-item.effects';
import { projectHumanResourceReducer } from '@/core/store/project-human-resource/project-human-resource.reducer';
import { ProjectHumanResourceEffects } from '@/core/store/project-human-resource/project-human-resource.effects';
import { equipmentProjectReducer } from '@/core/store/equipment-project/equipment-project.reducer';
import { EquipmentProjectEffects } from '@/core/store/equipment-project/equipment-project.effects';
import { providerReducer } from '@/core/store/provider/provider.reducer';
import { ProviderEffects } from '@/core/store/provider/provider.effects';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { registerLocaleData } from '@angular/common';
import { provideGoogleCharts } from 'angular-google-charts';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';

registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeEs, 'es-ES');

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        { provide: LOCALE_ID, useValue: 'es-ES' },

        DialogService,
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withEnabledBlockingInitialNavigation(),
            withComponentInputBinding()
        ),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            // Después de MsalInterceptor: este solo traduce la respuesta de error a un mensaje,
            // no toca la request. Ver auth-error.interceptor.ts.
            provide: HTTP_INTERCEPTORS,
            useClass: AuthErrorInterceptor,
            multi: true,
        },
        {
            // Devuelve la MISMA instancia que `main.ts` ya inicializó antes del bootstrap: la factory
            // es un singleton de módulo. Nada de `provideAppInitializer` acá — ver `initializeApp.ts`.
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        // Aplica el tema guardado (primary/surface/esquemas de cromo) antes del
        // primer render, para que no se vea un flash del look por defecto. El
        // claro/oscuro va por otro camino: LayoutService lo resuelve en un
        // `static` que corre incluso antes de esto.
        provideAppInitializer(() => {
            const injector = inject(Injector);
            const themeRuntime = inject(ThemeRuntimeService);
            afterNextRender(() => themeRuntime.init(), { injector });
        }),
        ConfirmationService,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        MessageService,
        provideStore(
            {
                router: routerReducer,
                'categoryType': categoryTypeReducer,
                'projectType': projectTypeReducer,
                'projectSubtype': projectSubTypeReducer,
                'unitMeasure': unitMeasureReducer,
                'apuStandard': apuStandardReducer,
                'apuStandardCategoryType': apuStandardCategoryTypeReducer,
                'apuStandardCategory': apuStandardCategoryReducer,
                'apuStandardResource': apuStandardResourceReducer,
                'apuCategory': apuCategoryReducer,
                'apuCategoryType': apuCategoryTypeReducer,
                'apuResource': apuResourceReducer,
                'tender': tenderReducer,
                'tenderSale': tenderSaleReducer,
                'product': productReducer,
                'projectChangeControl': projectChangeControlReducer,
                'project': projectReducer,
                'lps': lpsReducer,
                'budgetControlItem': budgetControlItemReducer,
                'projectHumanResource': projectHumanResourceReducer,
                'equipmentProject': equipmentProjectReducer,
                'provider': providerReducer,
            }
        ),
        provideEffects(
            [
                CategoryTypeEffects,
                ProjectSubtypeEffects,
                ProjectTypeEffects,
                UnitMeasureEffects,
                ApuStandardEffects,
                ApuStandardCategoryTypeEffects,
                ApuStandardCategoryEffects,
                ApuStandardResourceEffects,
                TenderEffects,
                TenderSaleEffects,
                ApuResourceEffects,
                ApuCategoryEffects,
                ApuCategoryTypeEffects,
                ProductEffects,
                ProjectChangeControlEffects,
                ProjectEffects,
                LpsEffects,
                BudgetControlItemEffects,
                ProjectHumanResourceEffects,
                EquipmentProjectEffects,
                ProviderEffects,
            ],
        ),
        provideRouterStore(),
        provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Sym70Preset,
                options: {
                    darkModeSelector: '.app-dark',
                    cssLayer: { name: 'primeng', order: 'tailwind-base, primeng, brand-base, tailwind-utilities' },
                },
            },
            ripple: true,
            translation: {
                dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
                dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
                dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
                monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
                monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
                today: 'Hoy',
                clear: 'Limpiar',
                dateFormat: 'dd/mm/yy',
                firstDayOfWeek: 1,
            }
        }),
        importProvidersFrom([
            NgxSpinnerModule.forRoot({ type: 'ball-fussion' })
        ]),
        provideCharts(withDefaultRegisterables()),
        provideGoogleCharts()
    ]
};